import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Input } from '../ui/input';
import { Upload, Trash2, Loader2, ImageIcon, X, CheckCircle, Pencil, Check, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EventPhotosTab = () => {
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEventTitle, setSelectedEventTitle] = useState('');
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, percent: 0, status: '' });
  const [uploadError, setUploadError] = useState('');
  const [previewFiles, setPreviewFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPhotoId, setEditingPhotoId] = useState(null);
  const [editPhotoName, setEditPhotoName] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchEvents();
    fetchPhotos();
  }, []);

  const fetchEvents = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/admin/events`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
    }
  };

  const fetchPhotos = async (eventId = null) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = eventId ? `${API_URL}/api/admin/event-photos?event_id=${eventId}` : `${API_URL}/api/admin/event-photos`;
      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(res.data.photos || []);
    } catch (err) {
      console.error('Failed to fetch photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEventSelect = (eventId) => {
    if (eventId === 'all') {
      setSelectedEventId('');
      setSelectedEventTitle('');
      fetchPhotos();
    } else {
      setSelectedEventId(eventId);
      const event = events.find(e => e.id === eventId);
      setSelectedEventTitle(event?.title || '');
      fetchPhotos(eventId);
    }
  };

  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    const previews = imageFiles.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name
    }));
    setPreviewFiles(prev => [...prev, ...previews]);
  };

  const removePreview = (index) => {
    setPreviewFiles(prev => {
      const updated = [...prev];
      URL.revokeObjectURL(updated[index].preview);
      updated.splice(index, 1);
      return updated;
    });
  };

  // Upload a single photo directly to S3 via presigned URL
  const uploadPhotoDirect = async (file, token, index, total) => {
    // Step 1: Get presigned URL
    const res = await axios.post(`${API_URL}/api/admin/event-photos/get-upload-url`, {
      filename: file.name,
      event_id: selectedEventId,
      event_title: selectedEventTitle,
      content_type: file.type || 'image/jpeg'
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const { upload_url, photo_url, filename } = res.data;

    // Step 2: Upload directly to S3 via XHR (no Axios — avoids JWT header issue)
    await new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', upload_url, true);
      xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const filePct = Math.round((e.loaded / e.total) * 100);
          const overallPct = Math.round(((index + filePct / 100) / total) * 100);
          setUploadProgress(prev => ({
            ...prev,
            status: `Uploading ${file.name}: ${filePct}% (${(e.loaded / (1024*1024)).toFixed(1)}/${(e.total / (1024*1024)).toFixed(1)} MB)`,
            percent: overallPct
          }));
        }
      };

      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Network error during S3 upload'));
      xhr.send(file);
    });

    // Step 3: Confirm with backend (saves metadata to DB)
    await axios.post(`${API_URL}/api/admin/event-photos/confirm-upload`, {
      photo_url,
      filename,
      event_id: selectedEventId,
      event_title: selectedEventTitle
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });

    return true;
  };

  const handleUpload = async () => {
    if (!selectedEventId) { toast.error('Please select an event first'); return; }
    if (previewFiles.length === 0) { toast.error('No photos selected'); return; }

    setUploading(true);
    setUploadError('');
    const total = previewFiles.length;
    let uploaded = 0;
    let failed = 0;
    setUploadProgress({ current: 0, total, percent: 0, status: 'Starting uploads...' });

    const token = localStorage.getItem('token');

    for (let i = 0; i < total; i++) {
      try {
        await uploadPhotoDirect(previewFiles[i].file, token, i, total);
        uploaded++;
        setUploadProgress(prev => ({
          ...prev,
          current: uploaded,
          percent: Math.round(((i + 1) / total) * 100),
          status: `${uploaded} of ${total} complete`
        }));
      } catch (err) {
        console.error(`Failed to upload ${previewFiles[i].name}:`, err);
        failed++;
        setUploadError(`Failed: ${previewFiles[i].name} - ${err.message}`);
      }
    }

    if (uploaded > 0) {
      toast.success(`${uploaded} photo(s) uploaded!`);
      // Send single batch notification instead of per-photo
      try {
        await axios.post(`${API_URL}/api/admin/event-photos/batch-complete`, {
          event_title: selectedEventTitle,
          count: uploaded
        }, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) { /* notification is non-critical */ }
    }
    if (failed > 0) toast.error(`${failed} photo(s) failed`);

    setPreviewFiles([]);
    setUploading(false);
    fetchPhotos(selectedEventId);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDeletePhoto = async (photoId) => {
    if (!confirm('Delete this photo?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/event-photos/${photoId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      toast.success('Photo deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleRenamePhoto = async (photoId) => {
    if (!editPhotoName.trim()) { setEditingPhotoId(null); return; }
    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API_URL}/api/admin/event-photos/${photoId}/rename`,
        { filename: editPhotoName.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, filename: editPhotoName.trim() } : p));
      toast.success('Photo renamed');
    } catch { toast.error('Failed to rename'); }
    setEditingPhotoId(null);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files).filter(f => f.type.startsWith('image/'));
    const previews = files.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name
    }));
    setPreviewFiles(prev => [...prev, ...previews]);
  }, []);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="font-display uppercase tracking-tight flex items-center gap-2">
            <ImageIcon className="w-5 h-5" />
            Event Photos
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => fetchPhotos(selectedEventId || null)} data-testid="refresh-photos-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={async () => {
            try {
              const token = localStorage.getItem('token');
              const res = await axios.post(`${API_URL}/api/admin/event-photos/generate-thumbnails`, {}, { headers: { Authorization: `Bearer ${token}` } });
              toast.success(res.data.message);
            } catch { toast.error('Failed to start thumbnail generation'); }
          }} data-testid="generate-thumbs-btn">
            <ImageIcon className="w-4 h-4 mr-1" /> Generate Thumbnails
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Event Selector */}
        <div className="space-y-2">
          <label className="text-sm font-medium">Select Event</label>
          <Select value={selectedEventId} onValueChange={handleEventSelect}>
            <SelectTrigger data-testid="event-photo-select">
              <SelectValue placeholder="Choose an event to upload photos for..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {events.map(event => (
                <SelectItem key={event.id} value={event.id}>
                  {event.title} {event.event_date ? `(${event.event_date})` : '(TBA)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Upload Area */}
        {selectedEventId && (
          <div
            className="border-2 border-dashed rounded-lg p-8 text-center hover:border-primary/50 transition-colors"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            data-testid="photo-upload-dropzone"
          >
            <ImageIcon className="w-12 h-12 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground mb-3">Drag & drop photos here or click to browse</p>
            <Button variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="photo-browse-btn">
              <Upload className="w-4 h-4 mr-2" /> Select Photos
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handleFilesSelected}
              className="hidden"
              data-testid="photo-file-input"
            />
          </div>
        )}

        {/* Preview */}
        {previewFiles.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-medium">{previewFiles.length} photo(s) ready</h4>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2">
              {previewFiles.map((pf, idx) => (
                <div key={idx} className="relative aspect-square rounded overflow-hidden group">
                  <img src={pf.preview} alt={pf.name} className="w-full h-full object-cover" />
                  {!uploading && (
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-1 right-1 h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => removePreview(idx)}
                    >
                      <X className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
            {!uploading && (
              <Button onClick={handleUpload} className="w-full" data-testid="upload-photos-btn">
                <Upload className="w-4 h-4 mr-2" />
                Upload {previewFiles.length} Photo{previewFiles.length > 1 ? 's' : ''}
              </Button>
            )}
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm font-medium">
                {uploadProgress.current} of {uploadProgress.total} done
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.percent}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground">{uploadProgress.status}</p>
          </div>
        )}

        {/* Error */}
        {uploadError && !uploading && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="photo-upload-error">
            <p className="text-sm font-medium text-red-800">Upload Error</p>
            <p className="text-sm text-red-600 mt-1">{uploadError}</p>
            <Button variant="outline" size="sm" className="mt-2" onClick={() => setUploadError('')}>Dismiss</Button>
          </div>
        )}

        {/* Uploaded Photos */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium">
              {selectedEventId ? `Photos for "${selectedEventTitle}"` : 'All Event Photos'}
            </h3>
            {!selectedEventId && (
              <Badge variant="secondary">{photos.length} total</Badge>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ImageIcon className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p className="text-sm">No photos uploaded yet</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
              {photos.map(photo => (
                <div key={photo.id} className="relative group" data-testid={`admin-photo-${photo.id}`}>
                  <div className="aspect-square rounded overflow-hidden">
                    <img
                      src={photo.url.startsWith('/uploads') ? `${API_URL}${photo.url}` : photo.url}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => { setEditingPhotoId(photo.id); setEditPhotoName(photo.filename); }}
                        data-testid={`rename-photo-${photo.id}`}
                      >
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeletePhoto(photo.id)}
                        data-testid={`delete-photo-${photo.id}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                  {editingPhotoId === photo.id ? (
                    <div className="flex items-center gap-1 mt-1">
                      <Input
                        value={editPhotoName}
                        onChange={(e) => setEditPhotoName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleRenamePhoto(photo.id); if (e.key === 'Escape') setEditingPhotoId(null); }}
                        className="h-6 text-[10px] px-1"
                        autoFocus
                        data-testid={`rename-photo-input-${photo.id}`}
                      />
                      <button onClick={() => handleRenamePhoto(photo.id)} className="shrink-0" data-testid={`rename-photo-save-${photo.id}`}>
                        <Check className="w-3 h-3 text-green-600" />
                      </button>
                    </div>
                  ) : (
                    <p className="text-[10px] text-muted-foreground mt-1 truncate cursor-pointer hover:text-primary" onClick={() => { setEditingPhotoId(photo.id); setEditPhotoName(photo.filename); }}>
                      {photo.filename}
                    </p>
                  )}
                  {!selectedEventId && editingPhotoId !== photo.id && (
                    <p className="text-[10px] text-muted-foreground/60 truncate">{photo.event_title}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default EventPhotosTab;
